// Producer characters editor.
//
// One row per character with inline edits to name + line rate + pickup
// terms + actor assignment. Optimistic updates so the project budget
// header at the top updates on every keystroke.

import { Button, Card, Drawer, EmptyState, Input, Modal, Spinner } from '@ohear/ui'
import { createFileRoute } from '@tanstack/react-router'
import { useMemo, useState } from 'react'
import { CharacterRowEditor } from '../../../features/characters'
import { RateImportForm } from '../../../features/script-book/RateImportForm'
import {
  totalBudgetUsd,
  useAssignActor,
  useCharacters,
  useInviteActor,
  useUpdateCharacter,
} from '../../../lib/api/characters'
import { useSendContractForSigning } from '../../../lib/api/contracts'
import { useProducerProject } from '../../../lib/api/producer-projects'
import type { CharacterRow } from '../../../lib/api/producer-types'
import { useRequireRole } from '../../../lib/auth'

export const Route = createFileRoute('/producer/projects/$projectId/characters')({
  component: CharactersPage,
})

function CharactersPage() {
  useRequireRole(['owner', 'member'])
  const { projectId } = Route.useParams()
  const projectQ = useProducerProject(projectId)
  const charactersQ = useCharacters(projectId)
  const updateCharacter = useUpdateCharacter(projectId)
  const assignActor = useAssignActor(projectId)
  const inviteActor = useInviteActor(projectId)
  const sendContract = useSendContractForSigning(projectId)

  const [inviteFor, setInviteFor] = useState<CharacterRow | null>(null)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviteName, setInviteName] = useState('')
  const [busySendId, setBusySendId] = useState<string | null>(null)
  const [rateImportOpen, setRateImportOpen] = useState(false)

  const characters = charactersQ.data ?? []
  const projectedBudget = useMemo(() => totalBudgetUsd(characters), [characters])

  // Build the actor option list from contracts the org already has
  // assigned. Each character carries its own assigned_actor_email so we
  // can populate the dropdown without an extra fetch. Producers see the
  // friendly list; the server validates membership server-side.
  const actorOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const c of characters) {
      if (c.assigned_actor_id && c.assigned_actor_email) {
        seen.set(c.assigned_actor_id, c.assigned_actor_email)
      }
    }
    return Array.from(seen, ([id, email]) => ({ id, label: email }))
  }, [characters])

  const onSendContract = async (characterId: string) => {
    const character = characters.find((c) => c.id === characterId)
    if (!character?.contract_id) return
    setBusySendId(characterId)
    try {
      await sendContract.mutateAsync(character.contract_id)
    } finally {
      setBusySendId(null)
    }
  }

  if (projectQ.isLoading || charactersQ.isLoading) {
    return (
      <Card>
        <div className="flex items-center gap-2 text-[13px] text-[var(--text-2)]">
          <Spinner /> Loading characters…
        </div>
      </Card>
    )
  }

  const assignedCount = characters.filter((c) => c.assigned_actor_id).length

  return (
    <section className="flex flex-col gap-4">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div className="flex flex-col gap-1 min-w-0">
          <div className="text-[12px] uppercase tracking-wide text-[var(--text-3)]">Characters</div>
          <h1 className="text-[22px] font-semibold tracking-tight text-[var(--text)] m-0 truncate">
            {projectQ.data?.name ?? ''}
          </h1>
          <p className="text-[13px] text-[var(--text-2)] m-0">
            {assignedCount}/{characters.length} assigned · projected budget{' '}
            <span className="font-semibold text-[var(--text)] tabular-nums">
              {formatUsd(projectedBudget)}
            </span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="ghost" onClick={() => setRateImportOpen(true)}>
            Import rates from CSV
          </Button>
        </div>
      </header>

      {characters.length === 0 ? (
        <EmptyState
          title="No characters yet"
          description="Approve the script book to populate the character list."
        />
      ) : (
        <Card dense data-tour="rate-editor">
          <div className="overflow-x-auto">
            <table className="w-full text-[13px] border-separate" style={{ borderSpacing: 0 }}>
              <thead className="sticky top-0 z-10" style={{ background: 'var(--bg-3)' }}>
                <tr className="text-left text-[11px] uppercase tracking-wide text-[var(--text-2)]">
                  <th className="px-3 py-2 font-medium">Code</th>
                  <th className="px-3 py-2 font-medium">Display name</th>
                  <th className="px-3 py-2 font-medium">Actor</th>
                  <th className="px-3 py-2 font-medium text-right">Lines</th>
                  <th className="px-3 py-2 font-medium">Line rate</th>
                  <th className="px-3 py-2 font-medium">Pickup mode</th>
                  <th className="px-3 py-2 font-medium text-right">Pickup rate</th>
                  <th className="px-3 py-2 font-medium">Contract</th>
                  <th className="px-3 py-2" />
                </tr>
              </thead>
              <tbody>
                {characters.map((row) => (
                  <CharacterRowEditor
                    key={row.id}
                    row={row}
                    actorOptions={actorOptions}
                    onChange={(patch) =>
                      void updateCharacter.mutateAsync({ character_id: row.id, ...patch })
                    }
                    onAssignActor={(actorId) =>
                      void assignActor.mutateAsync({ character_id: row.id, actor_id: actorId })
                    }
                    onInviteActor={() => {
                      setInviteFor(row)
                      setInviteEmail('')
                      setInviteName('')
                    }}
                    onSendForSigning={() => void onSendContract(row.id)}
                    busySend={busySendId === row.id}
                  />
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      <Drawer
        open={rateImportOpen}
        onClose={() => setRateImportOpen(false)}
        side="right"
        width={520}
        title="Import rates from CSV"
      >
        <RateImportForm projectId={projectId} onDone={() => setRateImportOpen(false)} />
      </Drawer>

      <Modal
        open={inviteFor != null}
        onClose={() => setInviteFor(null)}
        title={`Invite actor for ${inviteFor?.display_name ?? ''}`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setInviteFor(null)}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={inviteActor.isPending}
              disabled={!inviteEmail.includes('@') || !inviteFor}
              onClick={async () => {
                if (!inviteFor) return
                await inviteActor.mutateAsync({
                  character_id: inviteFor.id,
                  email: inviteEmail.trim(),
                  display_name: inviteName.trim() || null,
                })
                setInviteFor(null)
              }}
            >
              Send invite
            </Button>
          </>
        }
      >
        <div className="space-y-3">
          <div>
            <label htmlFor="inv-email" className="mb-1 block text-[13px] text-[var(--text)]">
              Email address
            </label>
            <Input
              id="inv-email"
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              placeholder="actor@example.com"
            />
          </div>
          <div>
            <label htmlFor="inv-name" className="mb-1 block text-[13px] text-[var(--text)]">
              Display name (optional)
            </label>
            <Input
              id="inv-name"
              value={inviteName}
              onChange={(e) => setInviteName(e.target.value)}
              placeholder="Jane Smith"
            />
          </div>
        </div>
      </Modal>
    </section>
  )
}

function formatUsd(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value)
}
